import { useEffect, useMemo } from 'react'
import { QueryClient, QueryClientProvider, useQuery, useQueryClient } from '@tanstack/react-query'
import { I18nextProvider, useTranslation } from 'react-i18next'
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation, useNavigate } from 'react-router'
import { ApiError, api, setUnauthorizedHandler, shouldRetry } from '@/api/client'
import { setCsrfToken } from '@/api/session'
import { Button } from '@/kit'
import { loadExtension } from '@/extensions/loader'
import type { ExtensionView, Locale } from '@/extensions/types'
import { TenantContext } from '@/sdk/tenant'
import { useActivityTracking } from './activity'
import { APP_BASE } from './base'
import { ExtensionBoundary } from './ExtensionBoundary'
import i18n from './i18n'
import { LoginPage } from './LoginPage'
import { AppFooter } from './shell/AppFooter'
import { TopNav } from './shell/TopNav'
import { platformVersion, UpdateNotice } from './shell/UpdateNotice'
import { fetchExtensionManifest, fetchSession, fetchTenantSettings, tenantSlugFromHost } from './tenant'
import type { SessionInfo, TenantSettings } from './tenant'

const queryClient = new QueryClient({
    defaultOptions: { queries: { retry: shouldRetry, refetchOnWindowFocus: false } },
})

function viewTitle(view: ExtensionView, locale: string): string {
    return view.title[locale as Locale] ?? view.title.en ?? view.id
}

function Message({ testId, children }: { testId: string; children: string }) {
    return (
        <p data-testid={testId} role="status" className="m-0 px-[var(--layout-page-padding-x)] py-[32px] text-[14px] leading-[20px] font-normal text-[var(--text-weak)]">
            {children}
        </p>
    )
}

const LANGUAGES = ['de', 'et', 'en']
const LANGUAGE_LABELS: Record<string, string> = { en: 'English', de: 'Deutsch', et: 'Eesti' }

/** The viewer's language choice, per tenant and browser (a convenience: may be unavailable). */
function storedLanguage(slug: string): string | null {
    try {
        return window.localStorage.getItem(`distribuai.language.${slug}`)
    } catch {
        return null
    }
}
function storeLanguage(slug: string, code: string) {
    try {
        window.localStorage.setItem(`distribuai.language.${slug}`, code)
    } catch {
        // Private mode or blocked storage: the choice just isn't remembered.
    }
}

/** Sales demos: tenants flagged `demo`, and every tenant on a demo host. */
function isDemo(settings: TenantSettings['data']): boolean {
    return settings.theme?.demo === true || /\.demo\.(distribuai\.eu|localhost)$/.test(window.location.hostname)
}

/** Signed-in layout: top bar from the tenant's views, the page, the footer. */
function AppLayout({
    tenant,
    logoHeight,
    demoLabel,
    languages,
    language,
    onLanguage,
    session,
    views,
    extensionProblem,
    extensionVersion,
}: {
    tenant: TenantSettings['data']['tenant']
    logoHeight?: number | null
    demoLabel: string | null
    languages: string[]
    language: string | null
    onLanguage: (code: string) => void
    session: SessionInfo['data']
    views: ExtensionView[]
    extensionProblem: boolean
    extensionVersion: string | null
}) {
    const { t, i18n: i18nInstance } = useTranslation()
    const navigate = useNavigate()
    const location = useLocation()
    const client = useQueryClient()
    const current = views.find((view) => location.pathname === view.path || location.pathname.startsWith(`${view.path}/`))
    const tenantInfo = useMemo(() => ({ slug: tenant.slug, name: tenant.name, demo: demoLabel !== null }), [tenant.slug, tenant.name, demoLabel])

    useActivityTracking({
        enabled: true,
        pathname: location.pathname,
        search: location.search,
        routeKey: current?.id ?? 'unknown',
        title: current ? viewTitle(current, i18nInstance.language) : '',
    })

    // Which build this tab runs (support asks; a stale tab is the usual "I don't see it").
    const platform = platformVersion()
    const versionLabel = platform ? [t('app.version', { version: platform }), extensionVersion !== platform ? extensionVersion : null].filter(Boolean).join(' · ') : null

    async function signOut() {
        try {
            await api.post('/api/auth/logout.php', {}, { handleUnauthorized: false })
        } catch {
            // The session may already be gone; sign out locally either way.
        }
        setCsrfToken(null)
        client.clear()
        navigate('/login', { replace: true })
    }

    return (
        <div className="flex min-h-screen flex-col bg-[var(--surface-default)]">
            <TopNav
                logoUrl={tenant.logoUrl ?? undefined}
                tenantName={tenant.name}
                logoHeight={logoHeight}
                demoLabel={demoLabel}
                demoHint={demoLabel ? t('app.demoHint') : null}
                languages={languages.map((code) => ({ code, label: LANGUAGE_LABELS[code] ?? code }))}
                language={language ?? undefined}
                languageLabel={t('app.language')}
                onLanguage={onLanguage}
                userName={session.full_name || session.email}
                menuLabel={t('app.userMenu')}
                navMenuLabel={t('app.navMenu')}
                signOutLabel={t('app.signOut')}
                onSignOut={signOut}
                versionLabel={versionLabel}
                entries={views.filter((view) => view.placement !== 'user-menu').map((view) => ({ key: view.id, label: viewTitle(view, i18nInstance.language), to: view.path }))}
                menuLinks={views.filter((view) => view.placement === 'user-menu').map((view) => ({ key: view.id, label: viewTitle(view, i18nInstance.language), to: view.path }))}
            />
            <main className="flex w-full min-w-0 flex-1 flex-col">
                {extensionProblem && <Message testId="extension-not-loaded">{t('extension.notLoaded')}</Message>}
                <TenantContext.Provider value={tenantInfo}>
                    <Outlet />
                </TenantContext.Provider>
            </main>
            <AppFooter />
            <UpdateNotice slug={tenant.slug} extensionVersion={extensionVersion} />
        </div>
    )
}

function Shell() {
    const { t, i18n: i18nInstance } = useTranslation()
    const navigate = useNavigate()
    const location = useLocation()
    const client = useQueryClient()
    const slug = useMemo(() => tenantSlugFromHost(), [])

    // Any 401 from the API: the session is gone. Back to login, keeping where we were.
    useEffect(() => {
        setUnauthorizedHandler(() => {
            setCsrfToken(null)
            client.removeQueries({ queryKey: ['session'] })
            const here = `${window.location.pathname.slice(APP_BASE.length - 1)}${window.location.search}`
            navigate(`/login?returnTo=${encodeURIComponent(here)}`, { replace: true })
        })
        return () => setUnauthorizedHandler(null)
    }, [client, navigate])

    // Tenant settings and the session load in parallel.
    const settings = useQuery({
        queryKey: ['tenant-settings', slug],
        queryFn: ({ signal }) => fetchTenantSettings(slug!, signal),
        enabled: slug !== null,
        staleTime: Infinity,
    })
    // Languages the tenant offers (its default first) that the app has strings for. The
    // viewer's remembered choice wins; otherwise the tenant's default.
    const theme = settings.data?.data.theme
    const languages = useMemo(
        () => [...new Set([theme?.defaultLanguage, ...(theme?.availableLanguages ?? [])].filter((code): code is string => !!code && LANGUAGES.includes(code)))],
        [theme?.defaultLanguage, theme?.availableLanguages],
    )
    // The current language lives in i18next (useTranslation re-renders on a change).
    const applyLanguage = (code: string) => {
        void i18nInstance.changeLanguage(code)
        document.documentElement.lang = code
    }
    useEffect(() => {
        if (!slug || languages.length === 0) return
        const remembered = storedLanguage(slug)
        const code = remembered && languages.includes(remembered) ? remembered : languages[0]!
        void i18nInstance.changeLanguage(code)
        document.documentElement.lang = code
    }, [slug, languages, i18nInstance])
    const chooseLanguage = (code: string) => {
        applyLanguage(code)
        if (slug) storeLanguage(slug, code)
    }

    const session = useQuery({
        queryKey: ['session'],
        queryFn: ({ signal }) => fetchSession(signal),
    })
    const signedIn = session.isSuccess
    useEffect(() => {
        if (session.data) setCsrfToken(session.data.data.csrf_token)
    }, [session.data])

    // A tenant's bespoke code is only fetched for a signed-in user of that tenant.
    const manifest = useQuery({
        queryKey: ['extension-manifest', slug],
        queryFn: ({ signal }) => fetchExtensionManifest(slug!, signal),
        enabled: signedIn && slug !== null,
        staleTime: Infinity,
    })
    const extension = useQuery({
        queryKey: ['extension', manifest.data?.url],
        queryFn: () => loadExtension(manifest.data!),
        enabled: Boolean(manifest.data),
        staleTime: Infinity,
        retry: false,
    })
    const allViews = extension.data?.status === 'loaded' ? (extension.data.definition.views ?? []) : []
    // Views for sales demos only (e.g. the product tour) stay out of real tenants' apps.
    const demoTenant = settings.data ? isDemo(settings.data.data) : false
    const views = allViews.filter((view) => view.audience !== 'demo' || demoTenant)
    // "/" goes to the first view of the main navigation.
    const landing = views.find((view) => view.placement !== 'user-menu')
    const extensionProblem = manifest.isError || (extension.data !== undefined && extension.data.status !== 'loaded')
    const extensionSettled = manifest.isError || manifest.data === null || extension.data !== undefined

    if (!slug || settings.isError) return <Message testId="tenant-unknown">{t('app.tenantUnknown')}</Message>
    if (settings.isPending || session.isPending) return <Message testId="app-loading">{t('app.loading')}</Message>
    // Only a 401 means signed out. A server that can't be reached right now (a restart, a
    // deploy) is not a sign-out: say so and offer a reload, never the sign-in page.
    if (session.isError && !(session.error instanceof ApiError && session.error.status === 401)) {
        return (
            <div className="flex flex-col items-start gap-[12px] px-[var(--layout-page-padding-x)] py-[32px]">
                <p data-testid="session-unreachable" role="status" className="m-0 text-[14px] leading-[20px] font-normal text-[var(--text-weak)]">
                    {t('app.unreachable')}
                </p>
                <Button variant="secondary" onClick={() => void session.refetch()}>
                    {t('app.retry')}
                </Button>
            </div>
        )
    }
    const tenant = settings.data.data.tenant
    const logoHeight = settings.data.data.theme?.logoHeight
    const demoLabel = isDemo(settings.data.data) ? t('app.demo') : null

    return (
        <Routes>
            <Route
                path="/login"
                element={
                    signedIn ? (
                        <Navigate to="/" replace />
                    ) : (
                        <LoginPage
                            tenant={tenant}
                            logoHeight={logoHeight}
                            demoLabel={demoLabel}
                            demoHint={demoLabel ? t('app.demoHint') : null}
                            ssoProviders={settings.data.data.theme?.ssoProviders}
                        />
                    )
                }
            />
            <Route
                element={
                    signedIn ? (
                        <AppLayout
                            tenant={tenant}
                            logoHeight={logoHeight}
                            demoLabel={demoLabel}
                            languages={languages}
                            language={i18nInstance.language}
                            onLanguage={chooseLanguage}
                            session={session.data.data}
                            views={views}
                            extensionProblem={extensionProblem}
                            extensionVersion={manifest.data?.version ?? null}
                        />
                    ) : (
                        <Navigate to={`/login?returnTo=${encodeURIComponent(location.pathname + location.search)}`} replace />
                    )
                }
            >
                <Route index element={landing ? <Navigate to={landing.path} replace /> : extensionSettled ? null : <Message testId="app-loading">{t('app.loading')}</Message>} />
                {views.map((view) => (
                    <Route
                        key={view.id}
                        path={`${view.path}/*`}
                        element={
                            // Keyed: without it React reuses one boundary across routes,
                            // so a crashed view would also blank the next one.
                            <ExtensionBoundary key={view.id} viewId={view.id}>
                                <view.component />
                            </ExtensionBoundary>
                        }
                    />
                ))}
                <Route path="*" element={extensionSettled ? <Message testId="not-found">{t('app.notFound')}</Message> : <Message testId="app-loading">{t('app.loading')}</Message>} />
            </Route>
        </Routes>
    )
}

export function App() {
    return (
        <QueryClientProvider client={queryClient}>
            <I18nextProvider i18n={i18n}>
                <BrowserRouter basename={APP_BASE}>
                    <Shell />
                </BrowserRouter>
            </I18nextProvider>
        </QueryClientProvider>
    )
}
